import { ListingResource, MediaField } from '../../system/field';
import { BaseResourceManager } from '../BaseResourceManager';
import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { MediaCategory } from '../../system/media';
import { executeWithRetry } from '../utils';
import { useReso } from '../../contexts/ResoContext';

export class MediaResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[MediaField.MED_VW_KEY];
  }

  getItemNumber(): number | undefined {
    return this[MediaField.MEDIA_ITEM_NUMBER];
  }

  getListingKeyField(): string {
    return MediaField.RESOURCE_RECORD_KEY;
  }

  getListingKey(): string | undefined {
    return this[MediaField.RESOURCE_RECORD_KEY];
  }

  // Utility methods for media resources

  /**
   * Get the URL based on the requested size
   */
  getUrl(
    size: 'original' | 'medium' | 'thumb' = 'original',
  ): string | undefined {
    switch (size) {
      case 'original':
        return this[MediaField.MEDIA_URL];
      case 'medium':
        return this[MediaField.MEDIA_URL_MEDIUM];
      case 'thumb':
        return this[MediaField.MEDIA_URL_THUMB];
      default:
        return this[MediaField.MEDIA_URL];
    }
  }

  /**
   * Check if media processing is complete
   */
  isProcessed(): boolean {
    return this[MediaField.MEDIA_PROCESSING_STATUS] === 'PROCESSED';
  }

  /**
   * Get processing status
   */
  getProcessingStatus(): string | undefined {
    return this[MediaField.MEDIA_PROCESSING_STATUS];
  }

  /**
   * Check if this is an image
   */
  isImage(): boolean {
    return this[MediaField.MEDIA_TYPE]?.toLowerCase().includes('image');
  }

  /**
   * Get file name
   */
  getFileName(): string | undefined {
    return this[MediaField.MEDIA_FILE_NAME];
  }

  /**
   * Get display order
   */
  getDisplayOrder(): number | undefined {
    return this[MediaField.MEDIA_DISPLAY_ORDER];
  }

  /**
   * Get category
   */
  getCategory(): string | undefined {
    return this[MediaField.MEDIA_CATEGORY];
  }
}

export class MediaResourceManager extends BaseResourceManager<MediaResource> {
  constructor(
    resourceType: ListingResource,
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: {
      enableCwa?: boolean;
      useTranslation?: boolean;
    } = {},
  ) {
    super(resourceType, executeQuery, fieldTranslator, taskManager, options);
  }

  /**
   * Core mapping function from API data to MediaResource
   */
  protected mapFromApiCore(apiData: any): MediaResource {
    return new MediaResource(apiData);
  }

  /**
   * Core mapping function from MediaResource to API data
   */
  protected mapToApiCore(resource: MediaResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Get media resources filtered by category
   */
  public async getByCategory(
    ...categories: MediaCategory[]
  ): Promise<MediaResource[]> {
    const allMedia = await this.list();
    return allMedia.filter(
      (media) =>
        media[MediaField.MEDIA_CATEGORY] &&
        categories.includes(media[MediaField.MEDIA_CATEGORY] as MediaCategory),
    );
  }

  /**
   * Upload media content
   */
  public async uploadContent(
    resource: MediaResource,
    blob: Blob,
  ): Promise<MediaResource> {
    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'uploadContent', resourceId: resource.getKey() },
      async (taskId) => {
        try {
          const resourceKey = resource.getKey();
          if (!resourceKey) {
            throw new Error('Cannot upload content for media without a key');
          }

          // Upload content via API with retry
          await executeWithRetry(
            async () => {
              return this.executeQuery(
                `${this.getEntityName()}('${resourceKey}')/$value`,
                {
                  method: 'PUT',
                  body: blob,
                  headers: {
                    'Content-Type':
                      resource[MediaField.MEDIA_TYPE] || blob.type,
                  },
                  buffer: true,
                },
              );
            },
            {
              maxRetries: 3,
            },
          );

          // Get the updated resource with URLs
          const updatedResource = await this.getById(resourceKey);

          if (!updatedResource) {
            throw new Error(
              'Failed to retrieve updated media resource after content upload',
            );
          }

          return updatedResource;
        } catch (error) {
          console.error('Error uploading media content:', error);
          throw error;
        }
      },
    );
  }

  /**
   * Create a media resource with content
   */
  public async createWithContent(
    resource: MediaResource,
    blob: Blob,
  ): Promise<MediaResource> {
    return this.taskManager.trackTask(
      this.resourceType,
      'create',
      { resourceId: resource.getItemNumber() },
      async (taskId) => {
        try {
          // First create the media record
          const createdResource = await this.create(resource);

          // Then upload the content
          return this.uploadContent(createdResource, blob);
        } catch (error) {
          console.error('Error creating media with content:', error);
          throw error;
        }
      },
    );
  }

  /**
   * Poll for media processing completion
   */
  public async pollForProcessing(
    resource: MediaResource,
    options: {
      maxAttempts?: number;
      interval?: number;
      timeoutMs?: number;
    } = {},
  ): Promise<MediaResource> {
    const { maxAttempts = 30, interval = 2000, timeoutMs = 60000 } = options;

    return this.taskManager.trackTask(
      this.resourceType,
      'other',
      { taskName: 'poll', resourceId: resource.getKey() },
      async (taskId) => {
        try {
          let attempts = 0;
          const startTime = Date.now();

          while (attempts < maxAttempts && Date.now() - startTime < timeoutMs) {
            // Get fresh resource data
            const updatedResource = await this.getById(resource.getKey()!);

            if (!updatedResource) {
              throw new Error('Media resource no longer exists');
            }

            // For virtual tours, we don't need to wait for processing
            if (
              updatedResource[MediaField.MEDIA_CATEGORY] === MediaCategory.Tour
            ) {
              return updatedResource;
            }

            // For other media types, check if processing is complete
            if (updatedResource.isProcessed()) {
              return updatedResource;
            }

            // Wait before next attempt
            await new Promise((resolve) => setTimeout(resolve, interval));
            attempts++;
          }

          // Return the latest resource even if not fully processed
          const finalResource = await this.getById(resource.getKey()!);
          if (!finalResource) {
            throw new Error('Media resource no longer exists');
          }

          // If we got here, it timed out but we'll return the latest state
          return finalResource;
        } catch (error) {
          console.error('Error polling for media processing:', error);
          throw error;
        }
      },
    );
  }

  /**
   * Set a media item as the preferred/primary photo
   */
  public async setAsPreferred(resource: MediaResource): Promise<MediaResource> {
    if (resource[MediaField.MEDIA_CATEGORY] !== MediaCategory.Photo) {
      throw new Error('Only photos can be set as preferred');
    }

    return this.taskManager.trackTask(
      this.resourceType,
      'update',
      { taskName: 'setAsPreferred', resourceId: resource.getKey() },
      async (taskId) => {
        // Create updated resource with preferred flag
        const updatedResource = new MediaResource({
          ...resource,
          [MediaField.PREFERRED_PHOTO_YN]: true,
        });

        // Update this resource
        await this.update(updatedResource);

        // Now find and update any other photos to remove preferred status
        const allPhotos = await this.getByCategory(MediaCategory.Photo);

        for (const photo of allPhotos) {
          if (
            photo.getKey() !== resource.getKey() &&
            photo[MediaField.PREFERRED_PHOTO_YN] === true
          ) {
            const nonPreferredPhoto = new MediaResource({
              ...photo,
              [MediaField.PREFERRED_PHOTO_YN]: false,
            });

            await this.update(nonPreferredPhoto);
          }
        }

        return updatedResource;
      },
    );
  }
}

/**
 * Create a new MediaResource instance with required fields
 */
export function createMediaResource(
  data: Partial<Record<string, any>> = {},
): MediaResource {
  const defaults = {
    [MediaField.MEDIA_ITEM_NUMBER]: 0,
    [MediaField.MEDIA_DISPLAY_ORDER]: 0,
    [MediaField.MEDIA_CATEGORY]: MediaCategory.Photo,
  };

  return new MediaResource({
    ...defaults,
    ...data,
  });
}
